import React from 'react';

/**
 * Icon3D — Reusable 3D squircle icon component.
 * Replicates the same raised-button look as the app logo and Contact page icons.
 *
 * Props:
 *   icon      — Lucide icon component (required)
 *   colour    — colour key (default: 'turquoise')
 *   size      — 'sm' | 'md' | 'lg'  (default: 'md')
 *   className — extra classes on the outer wrapper
 */

const COLOURS = {
  turquoise: {
    gradient: 'var(--gradient-primary)',
    shadow: 'var(--shadow-primary)',
  },
  blue: {
    gradient: 'linear-gradient(160deg, rgb(147,197,253) 0%, rgb(59,130,246) 35%, rgb(29,78,216) 100%)',
    shadow: '0 8px 24px rgba(59,130,246,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  green: {
    gradient: 'linear-gradient(160deg, rgb(134,239,172) 0%, rgb(34,197,94) 35%, rgb(21,128,61) 100%)',
    shadow: '0 8px 24px rgba(34,197,94,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  purple: {
    gradient: 'linear-gradient(160deg, rgb(216,180,254) 0%, rgb(168,85,247) 35%, rgb(107,33,168) 100%)',
    shadow: '0 8px 24px rgba(168,85,247,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  orange: {
    gradient: 'linear-gradient(160deg, rgb(253,186,116) 0%, rgb(249,115,22) 35%, rgb(154,52,18) 100%)',
    shadow: '0 8px 24px rgba(249,115,22,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  amber: {
    gradient: 'linear-gradient(160deg, rgb(252,211,77) 0%, rgb(245,158,11) 35%, rgb(146,64,14) 100%)',
    shadow: '0 8px 24px rgba(245,158,11,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  red: {
    gradient: 'linear-gradient(160deg, rgb(252,165,165) 0%, rgb(239,68,68) 35%, rgb(153,27,27) 100%)',
    shadow: '0 8px 24px rgba(239,68,68,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  indigo: {
    gradient: 'linear-gradient(160deg, rgb(165,180,252) 0%, rgb(99,102,241) 35%, rgb(55,48,163) 100%)',
    shadow: '0 8px 24px rgba(99,102,241,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  rose: {
    gradient: 'linear-gradient(160deg, rgb(253,164,175) 0%, rgb(244,63,94) 35%, rgb(159,18,57) 100%)',
    shadow: '0 8px 24px rgba(244,63,94,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
  pink: {
    gradient: 'linear-gradient(160deg, rgb(249,168,212) 0%, rgb(236,72,153) 35%, rgb(131,24,67) 100%)',
    shadow: '0 8px 24px rgba(236,72,153,0.7), 0 4px 10px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.25), inset 0 2px 3px rgba(255,255,255,0.5), inset 0 -3px 6px rgba(0,0,0,0.3)',
  },
};

const SIZE_MAP = {
  xs:  { outer: 'w-6 h-6',   icon: 'w-3 h-3', radius: 'rounded-lg' },
  sm:  { outer: 'w-8 h-8',   icon: 'w-4 h-4', radius: 'rounded-xl' },
  md:  { outer: 'w-12 h-12', icon: 'w-6 h-6', radius: 'rounded-2xl' },
  lg:  { outer: 'w-16 h-16', icon: 'w-8 h-8', radius: 'rounded-2xl' },
  xl:  { outer: 'w-20 h-20', icon: 'w-10 h-10', radius: 'rounded-3xl' },
};

const Icon3D = ({ icon: Icon, colour = 'turquoise', size = 'md', className = '' }) => {
  const c = COLOURS[colour] || COLOURS.turquoise;
  const s = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <div
      className={`${s.outer} ${s.radius} flex items-center justify-center relative overflow-hidden flex-shrink-0 ${className}`}
      style={{ background: c.gradient, boxShadow: c.shadow }}
    >
      {/* Specular highlight replaces shimmer — static elements don't shimmer */}
      {/* Specular highlight — bright oval at top-centre */}
      <div
        className="absolute"
        style={{
          top: '3px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '65%',
          height: '40%',
          background: 'radial-gradient(ellipse at 50% 20%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.45) 40%, transparent 100%)',
          borderRadius: '50%',
          filter: 'blur(0.5px)',
        }}
      />
      {/* Bottom darkening — depth illusion */}
      <div
        className="absolute bottom-0 left-0 right-0 h-2/5"
        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)' }}
      />
      {/* Icon */}
      <Icon
        className={`${s.icon} text-white relative z-10`}
        style={{ filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.5))' }}
      />
    </div>
  );
};

export default Icon3D;
